/**
 * The 'Login' store.
 *
 * File: \src\components\login\LoginStorePinia.ts
 * Created Date: Thursday, April 28th 2022, 13:33:16
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: November 22nd 2024, 08:40:32
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2022 Liebherr-Hausgeräte GmbH
 */
import { UserControllerApi } from '@/api/apis';
import { CubeLogger } from '@/Logger';

import {
  AccessCheckResponse,
  AccessCheckResponseRoleEnum,
  Colleague,
  ColleagueResponse,
  UserInfoResponse
} from '@/api/models';

import EnvironmentHandler from '@/EnvironmentHandler';

import MessageBus from '@/MessageBus';
import router from '@/router/router';
import { UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client';
import { defineStore } from 'pinia';
import { CountryHelper } from '../countries/CountryHelper';
import { StateBoolean } from '@/composables/StateBoolean';
import AccessChecker from '@/components/login/AccessChecker';
import LoginErrorHelper from './LoginErrorHelper';

import { useApiStore } from '@/api/ApiStore';
import { useI18nStore } from '@/I18nStore';
import { useHomeStore } from '@/views/HomeStore';

// Typically the access token is renewed 60 seconds before it runs out. We leave some room.
export const renewalGap: number = 61 * 1000;

// These are the settings to get log output from the OIDC library
//Log.logger = console;
//Log.level = Log.DEBUG;

/**
 * Helps us determining the phase and controlling the UI
 *
 * @export
 * @enum {number}
 */
export enum LoginPhase {
  STARTING,
  LOGIN,
  LOGGED_IN,
  TIMEDOUT
}

/**
 * Stores the login state. The "user" field might not be needed.
 *
 * @export
 * @interface LoginState
 */
export interface LoginState {
  role: AccessCheckResponseRoleEnum;
  accessLevels: string[];

  user: any;
  userInfo: UserInfoResponse;
  colleagues: Colleague[];
  organizationId: string;
  externalLoginState: string;
  loginPhase: LoginPhase;
  initialLogin: boolean;
  colleaguesLoaded: StateBoolean;
  userManager: UserManager;
}

// The store stores the users login state.
export const useLoginStore = defineStore('loginStore', {
  state: (): LoginState => ({
    role: undefined,
    accessLevels: [],
    user: undefined,
    userInfo: undefined,
    colleagues: [],
    organizationId: '',
    externalLoginState: undefined,
    loginPhase: LoginPhase.STARTING,
    initialLogin: false,
    colleaguesLoaded: { value: false },
    userManager: undefined
  }),
  getters: {
    /**
     * Getter for the authentication state. This is just a convenience shortcut to the user manager.
     *
     * @returns {boolean} true if the user is authenticated.
     */
    isAuthenticated(): boolean {
      const logger = CubeLogger.$log.prefix('LoginStore::isAuthenticated');
      logger.debug('Authentication status: ' + (this.user !== ''));

      if (this.user) {
        return true;
      }

      return false;
    },

    /**
     * Checks whether the current user is a temporary admin.
     *
     * @returns {boolean} true if the user is a temp admin.
     */
    isTempAdminUser(): boolean {
      if (this.role) {
        return AccessChecker.isTempAdminRole(this.role);
      }

      return false;
    },

    /**
     * Checks whether the current user can access the "internal" things.
     *
     * @returns {boolean} true if the user can access.
     */
    isInternalAccessAllowed(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isInternalAccessAllowed(this.accessLevels);
      }

      return false;
    },
    /**
     * Checks whether the current user can access the support area.
     *
     * @param {LoginState} state The state.
     * @returns {boolean} true if the user can access.
     */
    isSupportCenterAccessAllowed(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isSupportCenterAccessAllowed(this.accessLevels);
      }

      return false;
    },

    /**
     * Checks whether the current user is a sales user.
     *
     * @param {LoginState} state The state.
     * @returns {boolean} true if the user is a sales user.
     */
    isSalesUser(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isSalesUser(this.accessLevels);
      }

      return false;
    },

    /**
     * Checks whether the current user is a business user.
     *
     * @param {LoginState} state The state.
     * @returns {boolean} true if the user is a business user.
     */
    isBusinessUser(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isBusinessUser(this.accessLevels);
      }

      return false;
    },

    /**
     * Checks whether the current user is a developer.
     *
     * @param {LoginState} state The state.
     * @returns {boolean} true if the user is a developer.
     */
    isDeveloper(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isDeveloper(this.accessLevels);
      }

      return false;
    },

    /**
     * Checks whether the current user is support user.
     *
     * @param {LoginState} state The state.
     * @returns {boolean} true if the user is a support user.
     */
    isSupportUser(): boolean {
      if (this.accessLevels) {
        return AccessChecker.isSupportUser(this.accessLevels);
      }

      return false;
    },

    /**
     * Returns whether the login is new or loaded from the local storage
     *
     * @returns {boolean} true if user logged in.
     */
    isInitialLogin(): boolean {
      return this.initialLogin;
    },

    /**
     * Convenience getter for the current user name.
     *
     * @returns {string} The name of the current user.
     */
    getCurrentUserName(): string {
      return this.user ? this.user?.profile.name : '';
    },

    /**
     * Convenience getter for the current user email address.
     *
     * @returns {string} The email address of the current user.
     */
    getCurrentUserEmail(): string {
      return this.user ? this.user?.profile.email : '';
    },

    /**
     * Convenience getter for the UPN of the user.
     *
     * @returns {string} The UPN of the current user.
     */
    getCurrentUserUpn(): string {
      return this.userInfo ? this.userInfo.user.userPrincipalName : '';
    },

    /**
     * Convenience getter for the mobile phone number of the user.
     *
     * @returns {string} The mobile phone number of the current user.
     */
    getCurrentUserMobile(): string {
      return this.userInfo ? this.userInfo.user.mobile : '';
    },

    /**
     * Checks whether the user has a phone number and whether the server estimated it to being valid.
     *
     *
     * @returns {boolean} true if there is a valid phone number.
     */
    isCurrentUserMobileValid(): boolean {
      return this.userInfo ? this.userInfo.user.mobileValid : false;
    },

    /**
     * Convenience getter for the telephone (different than mobile) number of the user.
     *
     * @returns {string} The telephone number of the current user.
     */
    getCurrentUserTelephone(): string {
      return this.userInfo ? this.userInfo.user.telephoneNumber : '';
    },

    /**
     * Checks whether the user has a telephone number (different from mobile)
     * and whether the server estimated it to being valid.
     *
     *
     * @returns {boolean} true if there is a valid telephone number.
     */
    isCurrentUserTelephoneValid(): boolean {
      return this.userInfo ? this.userInfo.user.telephoneNumberValid : false;
    },

    /**
     * Convenience getter for the current user login.
     * Currently we can only use the login name of the LIS login
     * and that's the email used during login appended to the name?
     *
     * @returns {string} The login of the current user.
     */
    getCurrentUserLogin(): string {
      return this.user ? this.user.profile.sub : undefined;
    },

    /**
     * Convenience getter for the current userInfo.
     *
     * @returns {string} The login of the current user.
     */
    getColleagues(): Array<Colleague> {
      return this.colleagues;
    },

    /**
     * Gets colleagues loaded info
     *
     * @returns {StateBoolean} A StateBoolean to determine whether colleagues were loaded.
     */
    getColleaguesLoaded(): StateBoolean {
      return this.colleaguesLoaded;
    },

    /**
     * Gets the current bearer token.
     *
     * @return {*}  {(() => string | undefined)} The function that returns the bearer token.
     */
    getBearerToken(): () => string | undefined {
      return (): string | undefined => {
        let token: string;

        if (this.user !== '') {
          token = this.user.access_token;
        }

        return token;
      };
    },

    /**
     * Getter for the expiration of the login.
     *
     * @return {*}  {(() => Date | undefined)} The date or undefined.
     */
    getTokenExpiresAt(): () => Date | undefined {
      return (): Date => {
        let date: Date;

        if (this.user !== '') {
          date = new Date(this.user.expires_at * 1000);
        }

        return date;
      };
    },

    /**
     * Getter for the organization ID of the user's organization.
     *
     * @returns {string} The ID.
     */
    getOrganizationId(): string {
      return this.organizationId;
    },

    /**
     * Getter for the country code. Comes out of the LIS user object.
     *
     * @returns {string} The locale as reported by LIS.
     */
    getCurrentUserCountryCode(): string {
      const logger = CubeLogger.$log.prefix('LoginStore::getCurrentUserCountryCode');
      logger.debug('Country code', { country: this.userInfo?.user?.country });

      const userProfileCountryCode: string = this.userInfo?.user?.country;

      return CountryHelper.getCountryCode(userProfileCountryCode);
    },

    /**
     * Getter for the current logging phase. This can be:
     *
     * - STARTING (= system coming up)
     * - LOGIN (= user needs to go to the LIS login service)
     * - LOGGED_IN (= user is successfully logged in)
     * - TIMEDOUT (= the user session has timed out)
     *
     * @returns {LoginPhase} the login phase as explained above.
     */
    getLoginPhase(): LoginPhase {
      return this.loginPhase;
    }
  },

  actions: {
    /**
     * Checks the stored login. We store the login in sessionStorage. When the user accesses the application,
     * then the stored login might be still valid.
     *
     */
    checkStoredLogin() {
      const logger = CubeLogger.$log.prefix('LoginStore::checkStoredLogin');
      logger.debug('Checking whether there is a stored login for the user');

      this.initUserManager();
      // This is re-login when the user has left the site and comes back:
      const userStore: WebStorageStateStore = this.userManager.settings.userStore;

      userStore.getAllKeys().then(result => {
        if (result && result.length === 1) {
          userStore.get(result[0]).then(storedLogin => {
            const storedLoginAsJson = JSON.parse(storedLogin);

            // We take off 60 seconds to be on the safe side
            const storedExpirationDate: Date = new Date(storedLoginAsJson.expires_at * 1000);
            const now: Date = new Date();

            logger.debug('Stored token expires at:  ' + storedExpirationDate);
            logger.debug('Machine reports "now" as: ' + now);

            if (storedExpirationDate.getTime() > now.getTime() + renewalGap) {
              this.checkAccess(storedLoginAsJson, this.externalLoginState);
            } else {
              // Delete the stored key
              userStore.remove(result[0]);
              this.setLoginPhase(LoginPhase.LOGIN);
              router.replace({ name: 'Home' });
            }
          });
        } else {
          this.setLoginPhase(LoginPhase.LOGIN);
          router.replace({ name: 'Home' });
        }
      });
    },

    /**
     * Called to set the gateway ID. Just commits a mutation.
     *
     * @param externalLoginState The external login state as "<param>=<value>".
     */
    setExternalLoginState(externalLoginState: string) {
      const logger = CubeLogger.$log.prefix('LoginStore::setExternalLoginState');
      logger.debug('Setting external login state: ' + externalLoginState);

      this.storeExternalLoginState(externalLoginState);
    },

    /**
     * Called to start the login. We just let the user manager do the redirect to the Liebherr login page.
     *
     */
    loginStart(): void {
      this.initUserManager();
      const userManager: UserManager = this.userManager;

      // Here, we need to check the external login state which might be a BSDB gateway ID and a name.
      // If it is set, we need to pass it on to the login service as state.
      // We (after the login) get it back (see loginResult).
      if (this.externalLoginState !== undefined) {
        userManager.settings.extraQueryParams = {
          lh_login_source: EnvironmentHandler.BASE_URI + this.externalLoginState
        };

        userManager.signinRedirect({ state: this.externalLoginState });
      } else {
        userManager.settings.extraQueryParams = {
          lh_login_source: EnvironmentHandler.BASE_URI
        };

        userManager.signinRedirect();
      }
    },

    /**
     * Called by the IdentityServer after a successful login.
     *
     * We call loginResulLater() when the stores were loaded.
     *
     */
    loginResult(): void {
      const homeStore = useHomeStore();
      homeStore.initStores();

      this.loginResultLater();
    },

    /**
     * Called later to finish the user login.
     *
     */
    loginResultLater(): void {
      const logger = CubeLogger.$log.prefix('LoginStore::loginResultLater');
      logger.debug('Redirecting call back');

      this.initUserManager();

      const userManager: UserManager = this.userManager;

      // This call finishes the login of the user and fills the user object.
      userManager
        .signinRedirectCallback()
        .then(tempUser => {
          const externalLoginState: string = tempUser.state;

          logger.debug('Got external state: ' + externalLoginState);

          userManager.getUser().then(user => {
            if (user) {
              this.setInitialLogin(true);
              this.checkAccess(user, externalLoginState);
            } else {
              logger.debug('No user!');
            }
          });
        })
        .catch(reason => {
          logger.error('Login failed.', reason);
          this.loginStart();
        });
    },

    /**
     * Checks the user's access privileges by accessing the middle tier. If that succeeds and if the user has access
     * then we're completing the user initialization. Otherwise we stop.
     *
     * @param {*} [user, externalLoginState] The user and the external login state.
     */
    checkAccess(user, externalLoginState): void {
      const logger = CubeLogger.$log.prefix('LoginStore::checkAccess');
      logger.debug('Checking access for user: ' + user.profile.sub);

      // Set this (preliminarily) so that we can make a call to the backend.
      this.user = user;

      const userManager: UserManager = this.userManager;

      const apiStore = useApiStore();
      const userControllerApi: UserControllerApi = apiStore.getUserControllerApi;

      // The "later" call:
      userControllerApi
        .checkAccessUsingPOST()
        .then(response => {
          const accessCheckResponse: AccessCheckResponse = response;

          if (AccessChecker.isApplicationAccessAllowed(accessCheckResponse)) {
            logger.debug('User is allowed to access the app, storing...');

            // Store the context for further access to the backend
            apiStore.setContext(accessCheckResponse.context);
            this.storeAccessCheckData(accessCheckResponse);

            apiStore.setContext(accessCheckResponse.context);

            // "Eagle has landed"
            this.loginPhase = LoginPhase.LOGGED_IN;

            this.checkUser(user, externalLoginState);

            // When we type the url without any specific page, user is redirected to dashboard after log in.
            if (!externalLoginState) {
              router.replace({ name: 'Dashboard' });
            }
          } else {
            const reason: string = LoginErrorHelper.getErrorText(accessCheckResponse.role);
            const errorCase: string = accessCheckResponse.role;

            const keyValue: any = {
              reason: reason,
              errorCase: errorCase
            };

            this.denyAccess(keyValue);
          }
        })
        .catch(_error => {
          logger.debug('Call to checkUserUsingPOST failed');

          this.user = null;

          userManager.removeUser().then(() => {
            logger.debug('User removed');
            this.clearLoginData();
          });

          const i18n = useI18nStore();
          router.replace({ name: 'LoginError', params: { reason: i18n.t('biz.home.loginerror.message') } });
        });
    },

    /**
     * Checks the user's login by accessing the middle tier to get more information about the user.
     *
     * @param {*} [user, externalLoginState] The user and the external login state.
     */
    checkUser(user, externalLoginState): void {
      const logger = CubeLogger.$log.prefix('LoginStore::checkUser');
      logger.debug('Loading user data...');

      const apiStore = useApiStore();
      const userControllerApi: UserControllerApi = apiStore.getUserControllerApi;
      const userManager: UserManager = this.userManager;

      // The "later" call:
      userControllerApi
        .checkUserUsingPOST()
        .then(response => {
          const userInfo: UserInfoResponse = response;

          // Store the country
          apiStore.setCountry(CountryHelper.getCountryCode(userInfo.user.country));

          this.storeSuccessfulLogin(user, userInfo, externalLoginState);
        })
        .catch(error => {
          logger.debug('Call to checkUserUsingPOST failed', error);

          this.user = null;

          userManager.removeUser().then(() => {
            logger.debug('User removed');
            this.clearLoginData();
          });

          const i18n = useI18nStore();
          router.replace({ name: 'LoginError', params: { reason: i18n.t('biz.home.loginerror.message') } });
        });
    },

    /**
     * Get colleagues data from server and store it.
     *
     * @returns {void}
     */
    loadColleagues(): void {
      const logger = CubeLogger.$log.prefix('LoginStore::loadColleagues');
      logger.debug('Getting colleagues data...');

      const apiStore = useApiStore();

      const userControllerApi: UserControllerApi = apiStore.getUserControllerApi;
      const isTempAdmin: boolean = this.isTempAdminUser;

      //Additional check for temp admin user. Temp admin doesn't have collegues.
      if (isTempAdmin) {
        this.colleaguesLoaded.value = true;

        return;
      }

      userControllerApi
        .getColleaguesUsingGET()
        .then(response => {
          this.setColleagues(response);
        })
        .catch(_reason => {
          logger.debug('Call to getColleaguesUsingGET failed');
        });
    },

    /**
     * Changes the mobile number and it's validation into userInfo.
     *
     * @param {string} phoneNumber The new number.
     */
    setMobileNumber(response: string): void {
      // Make sure to update the phone number
      this.updatePhoneNumber(response);
    },

    /**
     * Called when we perform a logout. We call the user manager to clear the login and
     * clear our data as well.
     *
     */
    logoutStart(): void {
      const logger = CubeLogger.$log.prefix('LoginStore::logoutStart');
      logger.debug('Sign out redirecting...');

      const userManager: UserManager = this.userManager;

      userManager.stopSilentRenew();

      userManager
        .signoutRedirect()
        .then(_result => {
          logger.debug('Logout was successful');
        })
        .finally(() => {
          this.clearLoginData();
        });
    },

    /**
     * Clears the currently active login data in the state.
     *
     */
    clearLoginData(): void {
      const logger = CubeLogger.$log.prefix('LoginStore::clearLoginData');
      logger.debug('Clearing login data');

      this.user = '';
      this.userInfo = undefined;
      this.organizationId = '';
      this.externalLoginState = undefined;
      this.loginPhase = LoginPhase.STARTING;
    },

    /**
     * Action for calling the mutator setInitialLogin
     *
     * @param {boolean} value Initial login state.
     */
    setInitialLoginState(value: boolean) {
      this.setInitialLogin(value);
    },

    /**
     * Starts the silent renew for the user session.
     *
     */
    startSilentRenew(): void {
      this.setSilentRenew(true);
    },

    /**
     * Stops the silent renew for the user session.
     *
     */
    stopSilentRenew(): void {
      this.setSilentRenew(false);
    },

    /**
     * Called to deny access to the user. The reason is supposed to contain string
     * explaining to the user why we won't let him in.
     *
     * @param {string} reason The reason for the denied access.
     */
    denyAccess(keyValue: any): void {
      const logger = CubeLogger.$log.prefix('LoginStore::denyAccess');
      logger.debug('Access is denied!');

      // Clearing all data to make sure that the user is really logged-in
      this.user = null;
      const userManager: UserManager = this.userManager;

      userManager.removeUser().then(() => {
        logger.debug('User removed');
        this.clearLoginData();
      });

      router.replace({ name: 'LoginError', params: { reason: keyValue.reason } });
    },

    /**
     * Stores the external login state if passed as part of the call to the application.
     *
     * @param {string} extLoginState The external state (call sent by the BSDB).
     */
    storeExternalLoginState(extLoginState: string): void {
      const logger = CubeLogger.$log.prefix('LoginStore::storeExternalLoginState');
      logger.debug('Storing external login state: ' + extLoginState);

      this.externalLoginState = extLoginState;
    },

    /**
     * Stores the role the user has received from the backend.
     *
     * @param {AccessCheckResponse} accessCheckResponse The response containing the role.
     */
    storeAccessCheckData(accessCheckResponse: AccessCheckResponse): void {
      const logger = CubeLogger.$log.prefix('LoginStore::storeAccessCheckResponse');
      logger.debug('Storing accessCheckResponse - user role: ', { role: accessCheckResponse.role });

      this.role = accessCheckResponse.role;
      this.accessLevels = accessCheckResponse.accessLevels;
    },

    /**
     * Called when we have a successful login.
     *
     * - userToStore is the (internal) user id.
     * - userInfo is an object describing the user and his colleagues.
     * - externalLoginState is state that may have been part of the link that opened the app.
     *
     * @param {*} userToStore the user to store.
     * @param {*} userInfo the user info.
     * @param {*} _externalLoginState unused.
     */
    storeSuccessfulLogin(userToStore, userInfo, _externalLoginState): void {
      const logger = CubeLogger.$log.prefix('LoginStore::storeSuccessfulLogin');

      this.user = userToStore;
      this.userInfo = userInfo;
      this.organizationId = userToStore.profile.company_id;

      const userManager: UserManager = this.userManager;

      logger.debug('Stored token in state: ' + JSON.stringify(this.user.id_token));

      // When the token has expired, the only thing the user can do is re-login
      userManager.events.addAccessTokenExpired(() => {
        logger.debug('*** ACCESS TOKEN HAS EXPIRED ***');

        this.loginPhase = LoginPhase.TIMEDOUT;
      });

      userManager.events.addUserLoaded(user => {
        logger.debug('User was loaded: ' + JSON.stringify(user));

        this.user = user;
      });

      MessageBus.emitUserAuthenticated();
    },

    /**
     * Sets colleague data
     *
     * @param {ColleagueResponse} colleagueResponse The response.
     */
    setColleagues(colleagueResponse: ColleagueResponse) {
      const logger = CubeLogger.$log.prefix('LoginStore::setColleagues');
      logger.debug('Setting colleagues');

      this.colleagues = colleagueResponse.colleagues;
      this.colleaguesLoaded.value = true;
    },

    /**
     * Sets the login phase.
     *
     * @param {LoginPhase} phase The new phase of login.
     */
    setLoginPhase(phase: LoginPhase): void {
      const logger = CubeLogger.$log.prefix('LoginStore::setLoginPhase');
      logger.debug('Setting phase:' + phase);

      this.loginPhase = phase;
    },

    /**
     * Sets initial login state value
     *
     * @param {boolean} value A flag parameter.
     */
    setInitialLogin(value: boolean): void {
      this.initialLogin = value;
    },

    /**
     * Sets the silent renew.
     *
     * @param {boolean} value The new value.
     */
    setSilentRenew(value: boolean): void {
      const logger = CubeLogger.$log.prefix('LoginStore::setSilentRenew');
      logger.debug('Changing silent renew to ' + value);

      const userManager: UserManager = this.userManager;

      if (value) {
        userManager.startSilentRenew();
      } else {
        userManager.stopSilentRenew();
      }
    },

    /**
     * Updates mobile number in the store.
     *
     * @param {string} value updated mobile number.
     */
    updatePhoneNumber(value: string): void {
      if (value) {
        this.userInfo.user.mobile = value;
      }
    },

    /**
     * Initialize user manager for the OIDC provider. Called on every loginStart() and on every refresh of the app.
     *
     */
    initUserManager(): void {
      if (!this.userManager) {
        const store: WebStorageStateStore = new WebStorageStateStore({
          prefix: 'bus.',
          store: window.sessionStorage
        });

        console.log(EnvironmentHandler.AUTHORITY);

        const config: UserManagerSettings = {
          authority: EnvironmentHandler.AUTHORITY,
          client_id: EnvironmentHandler.CLIENT_ID,
          client_secret: EnvironmentHandler.CLIENT_SECRET,
          redirect_uri: EnvironmentHandler.REDIRECT_URI,
          response_type: EnvironmentHandler.RESPONSE_TYPE,
          scope: EnvironmentHandler.SCOPE,
          post_logout_redirect_uri: EnvironmentHandler.BASE_URI,
          silent_redirect_uri: EnvironmentHandler.SILENT_REDIRECT_URI,
          userStore: store
        };

        this.userManager = new UserManager(config);
      }
    }
  }
});
